import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'
import fetchCalculatedBusinessCredits from 'actions/businessTraveller/fetchCalculatedBusinessCredits'
import fetchItemPolicyCalculatedCredits from 'actions/businessTraveller/fetchItemPolicyCalculatedCredits'
import { generateOfferCreditKey } from 'businessTraveller/utils/generateOfferCreditKey'
import { generateItemPolicyOfferCreditKey } from 'businessTraveller/utils/generateItemPolicyCalculationKey'
import { mergeItemPolicyCalculatedCreditsWithOfferCredit } from 'businessTraveller/utils/mapItemPolicyRowsToOfferCredit'
import { selectItemPolicyOfferCredit } from 'businessTraveller/selectors/itemPolicy/selectItemPolicyOfferCredit'
import { selectSelectedTravellerEmployeeIds } from 'businessTraveller/selectors/itemPolicy/selectSelectedTravellerEmployeeIds'
import { getLocationDataFromOffer } from 'businessTraveller/utils/getLocationDataFromOffer'
import {
  hasHotelItemPolicyLocation,
  resolveHotelItemPolicyLocation,
} from 'businessTraveller/utils/resolveHotelItemPolicyLocation'
import { useItemPolicyShadowFetcher } from 'businessTraveller/hooks/itemPolicy/useItemPolicyShadowFetcher'
import GeoContext from 'contexts/geoContext'
import { usePlaceByCoords } from 'hooks/usePlace'
import useQueryParams from 'hooks/useQueryParams'
import { getCenterCoordinates } from 'lib/search/searchUtils'
import { getSearchItemFromURLSearchParams, parseSearchString } from 'lib/url/searchUrlUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useContext, useEffect, useMemo } from 'react'

interface Params {
  cost: number
  numberOfNights: number
  offer: App.AnyOffer
  roomCount?: number
  enableShadowCompare?: boolean
}

function useBusinessTravellerItemPolicyOfferCreditFetcher(params: Params) {
  const {
    cost,
    numberOfNights,
    offer,
    roomCount = 1,
    enableShadowCompare = true,
  } = params
  const { currentCurrency: currencyCode } = useContext(GeoContext)
  const appDispatch = useAppDispatch()
  const queryParams = useQueryParams()
  const search = useAppSelector(state => state.router.location.search)
  const employeeIds = useAppSelector(selectSelectedTravellerEmployeeIds)
  const businessId = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.business?.id)
  const offersCredits = useAppSelector(state => state.businessTraveller.offersCredits)
  const itemPolicyCalculatedCredits = useAppSelector(state => state.businessTraveller.itemPolicyCalculatedCredits)

  const approvedRequestId = useMemo(() => {
    const queryParamsFromSearch = parseSearchString(search)
    return queryParamsFromSearch.approvalRequest
  }, [search])

  const searchItem = useMemo(() => getSearchItemFromURLSearchParams(queryParams), [queryParams])
  const mapViewCoords = useMemo(() => {
    if (searchItem?.searchType === 'bounds') {
      const coords = searchItem.value?.split(',')
      if (coords && coords.length === 4) {
        const numCoords = coords.map(parseFloat)
        return getCenterCoordinates(numCoords as [number, number, number, number])
      }
    }
    return []
  }, [searchItem])
  const [place] = usePlaceByCoords(mapViewCoords[0], mapViewCoords[1])

  const location = useMemo(() => {
    const offerLocation = getLocationDataFromOffer(offer).location
    let resolvedLatitude = searchItem?.searchType === 'lat_long' ? searchItem.lat?.toString() : undefined
    let resolvedLongitude = searchItem?.searchType === 'lat_long' ? searchItem.lng?.toString() : undefined

    if (mapViewCoords.length >= 2) {
      resolvedLatitude = mapViewCoords[0].toString()
      resolvedLongitude = mapViewCoords[1].toString()
    }

    return resolveHotelItemPolicyLocation({
      latitude: offerLocation.latitude || resolvedLatitude,
      longitude: offerLocation.longitude || resolvedLongitude,
      placeFromId: place,
    }) ?? { latitude: '', longitude: '' }
  }, [mapViewCoords, offer, place, searchItem])

  const hasValidLocation = hasHotelItemPolicyLocation(location)

  const { cacheKey, item, legacyCreditKey, legacyItem } = useMemo(() => {
    const key = `hotel_${offer.id}_${cost}_${numberOfNights}_${roomCount}`
    const hotelItem: App.BusinessTraveller.ToBeCalculatedItemPolicyHotelItem = {
      itemPolicyType: 'HOTEL',
      key,
      cost,
      currencyCode,
      numberOfNights,
      roomCount,
      travellerCount: 1,
      location,
    }
    const legacyItem: App.BusinessTraveller.ToBeCalculatedBusinessCreditItem = {
      itemPolicyType: 'hotel',
      numberOfNights,
      cost,
      currencyCode,
      location,
      roomCount,
    }
    return {
      cacheKey: generateItemPolicyOfferCreditKey(hotelItem, offer.id, employeeIds),
      item: hotelItem,
      legacyCreditKey: generateOfferCreditKey(legacyItem, offer),
      legacyItem,
    }
  }, [cost, currencyCode, employeeIds, location, numberOfNights, offer, roomCount])

  useEffect(() => {
    appDispatch(fetchAccountBusiness())
  }, [appDispatch])

  useEffect(() => {
    if (businessId && employeeIds.length > 0 && hasValidLocation) {
      appDispatch(fetchItemPolicyCalculatedCredits([cacheKey, item, employeeIds], businessId))
    }
  }, [appDispatch, businessId, cacheKey, employeeIds, hasValidLocation, item])

  useEffect(() => {
    if (businessId) {
      appDispatch(
        fetchCalculatedBusinessCredits(
          [legacyCreditKey, legacyItem],
          businessId,
          approvedRequestId ?? '',
        ),
      )
    }
  }, [appDispatch, approvedRequestId, businessId, legacyCreditKey, legacyItem])

  const mappedOfferCredit = useAppSelector(state => selectItemPolicyOfferCredit(state, cacheKey))
  const itemPolicyData = itemPolicyCalculatedCredits?.[cacheKey]
  const legacyOfferCredit = offersCredits[legacyCreditKey]
  const offerCreditData = mergeItemPolicyCalculatedCreditsWithOfferCredit(mappedOfferCredit, itemPolicyData)

  useItemPolicyShadowFetcher({
    enabled: enableShadowCompare,
    businessId,
    cacheKey,
    item,
    employeeIds,
    vertical: 'hotel',
    surface: 'offer_credit',
    legacyData: legacyOfferCredit,
    itemPolicyData,
  })

  return {
    offerCredit: mappedOfferCredit,
    offerCreditData,
    cacheKey,
    item,
    itemPolicyData,
    legacyOfferCredit,
    employeeIds,
  }
}

export default useBusinessTravellerItemPolicyOfferCreditFetcher
